import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FullScreen } from 'react-full-screen';
import { useHotkeys } from 'react-hotkeys-hook';
import ReactPlayer from 'react-player';
import { useCloudFrontPlaybackRecovery } from '../../../helpers/useCloudFrontPlaybackRecovery';
import { useSidebarPlayerSeek } from '../../ClipFavourites/context';
import { useClipAngleLock } from '../../ClipFavourites/useClipAngleLock';
import { FavouriteFeedback } from '../../ClipFavourites/Star';
import { isNumber } from 'lodash';
import cx from 'classnames';
import PlayCircleOutlineOutlinedIcon from '@mui/icons-material/PlayCircleOutlineOutlined';

import { issueCloudFrontSignedCookies, shouldUseCredentialsForMediaUrl } from '../../../api';
import {
  findSearchValue,
  getComposerWebPreferences,
  getVideoQualityFromLocalStorage,
  isSafari,
  rememberManualVolumeOverrideForFeed,
  setVideoQualityToLocalStorage,
  shouldApplyMutePreferenceForFeed,
  subscribeComposerWebPreferences,
} from '../../../helpers';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { parseQueryString } from '../../../helpers/parseQueryString';
import { buildQueryString } from '../../../helpers/buildQueryString';
import { saveVideoLength } from '../../../store/videos/videoSlice';
import ComposerVideoControls from './ComposerVideoControls';
import PlayIcon from '../../../assets/icons/play-icon.svg';
import {
  toFollowPlayhead,
  updateCurrentVideoThatInFrame,
  updateSelectedVideoQuality,
  updateShowNotes,
} from '../../../store/sharedStore/sharedStoreSlice';
import { TClip } from '../../Timeline/TimelineClip';
import css from './ComposerVideoPlayer.module.scss';
import {
  setComposerMarkerPosition,
  triggerRefresh,
  updateComposerPlayControls,
  updateComposerVideoCLips,
  updateCurrentSelectedComposerClips,
  updateCustomTime,
  updateSelectedMultipleClips,
  updateVideoLength,
} from '../../../store/composers/composersSlice';
import FullPageLoader from '../FullPageLoader';
import { MOBILE_SWITCH_ANGLE_EVENT } from '../MobileControlCenter';
import NotesForVideo from '../NotesForVideo';
import { VideoDrawing } from '../../VideoDrawing';
import { COMMENT, VIDEO_TYPE } from '../../../enums';
import { CircularProgress } from '@mui/material';

type TComposerVideoPlayer = {
  videoUrl?: string;
  clipsCount: number;
  videoData?: VIDEO_TYPE;
  showCommentsSection: boolean;
  videoPlayerHeight: number;
  fullScreen: any;
};

const ignoreAngleShortcut = () =>
  Boolean(document.getElementById('clip-drawer-modal') || document.querySelector('[role="dialog"]'));

const ComposerVideoPlayer: React.FC<TComposerVideoPlayer> = ({
  videoUrl,
  clipsCount,
  videoData,
  showCommentsSection,
  videoPlayerHeight,
  fullScreen,
}) => {
  const PAUSE_OVERLAY_HIDE_EVENT = 'amplify:hide-pause-overlay';

  const videoPlayerRef = useRef<ReactPlayer | null>(null);
  const safariPlayerRefs = useRef<(ReactPlayer | null)[]>([]);
  const [showControls, setShowControls] = useState(false);
  const [loading, setLoading] = useState(false);
  const dispatch = useAppDispatch();
  const { composerPlayControl, currentComposerVideos, composerRows, customTime, refreshTrigger, composerVideoClips, currentSelectedComposerClips } = useAppSelector(
    (state) => state.composersStore,
  );
  const { showNotes, isSidebarHidden, showSidebar, selectedVideoQuality } = useAppSelector((state) => state.sharedStore);
  const { notes, qualifiers } = useAppSelector((state) => state.videoStore);
  const composerControlsRef = useRef(composerPlayControl.composerControls);
  const safari = isSafari();
  const [clipIndex, setClipIndex] = useState(0);
  const resolvedVideoUrl = videoUrl || '';
  const [playbackUrl, setPlaybackUrl] = useState('');
  const [playbackUrls, setPlaybackUrls] = useState<string[]>([]);
  const pendingSeekTargetRef = useRef<number | null>(null);
  const pendingAngleSeekRef = useRef<{ videoId: string; clipId: string; seconds: number } | null>(null);
  const ignoreProgressUntilRef = useRef<number>(0);
  const lastMuteAllVideosRef = useRef(getComposerWebPreferences().muteAllVideos);
  const feedId = findSearchValue('feedId');
  const clipToJumpQuery = findSearchValue('clipToJump');
  const autoPlayFromQuery = findSearchValue('autoplay');
  const consumedAutoplayRef = useRef<string | null>(null);

  const preferredQuality = selectedVideoQuality?.quality || getVideoQualityFromLocalStorage() || '';
  const resolveVideoUrl = (video?: VIDEO_TYPE | null) => {
    if (!video?.s3_keys) return '';
    if (preferredQuality && video.s3_keys[preferredQuality]) {
      return video.s3_keys[preferredQuality];
    }

    let highestKey = '';
    let highestValue = '';
    for (const key in video.s3_keys) {
      const value = video.s3_keys[key];
      if (value?.length && (!highestKey || parseInt(key) > parseInt(highestKey))) {
        highestKey = key;
        highestValue = value;
      }
    }

    return highestValue;
  };

  const angleVideos = useMemo(
    () => (composerVideoClips?.length ? composerVideoClips : currentComposerVideos),
    [composerVideoClips, currentComposerVideos],
  );

  const playbackSourceUrls = useMemo(() => {
    if (safari) {
      return angleVideos.map((video) => resolveVideoUrl(video));
    }

    return resolvedVideoUrl ? [resolvedVideoUrl] : [];
  }, [angleVideos, safari, preferredQuality, resolvedVideoUrl]);

  const activePlaybackUrl = safari ? playbackUrls[clipIndex] || playbackUrls[0] || '' : playbackUrl;
  const playbackRecovery = useCloudFrontPlaybackRecovery(activePlaybackUrl, videoPlayerRef, composerPlayControl.composerControls.playing);

  useEffect(() => {
    composerControlsRef.current = composerPlayControl.composerControls;
  }, [composerPlayControl.composerControls]);

  useEffect(() => {
    const applyMutePreference = (muteAllVideos: boolean) => {
      if (muteAllVideos) {
        if (!shouldApplyMutePreferenceForFeed(feedId)) return;
        dispatch(
          updateComposerPlayControls({
            ...composerControlsRef.current,
            volume: 0,
          }),
        );
        lastMuteAllVideosRef.current = true;
        return;
      }

      if (lastMuteAllVideosRef.current && composerControlsRef.current.volume === 0) {
        dispatch(
          updateComposerPlayControls({
            ...composerControlsRef.current,
            volume: 1,
          }),
        );
      }

      lastMuteAllVideosRef.current = false;
    };

    applyMutePreference(getComposerWebPreferences().muteAllVideos);
    return subscribeComposerWebPreferences((preferences) => {
      applyMutePreference(preferences.muteAllVideos);
    });
  }, [dispatch, feedId]);

  useEffect(() => {
    if (!getComposerWebPreferences().muteAllVideos) return;
    if (!shouldApplyMutePreferenceForFeed(feedId)) return;
    if (composerPlayControl.composerControls.volume === 0) return;

    dispatch(
      updateComposerPlayControls({
        ...composerPlayControl.composerControls,
        volume: 0,
      }),
    );
  }, [composerPlayControl.composerControls, dispatch, feedId]);

  useEffect(() => {
    let cancelled = false;

    const preparePlaybackUrls = async () => {
      if (!playbackSourceUrls.some(Boolean)) {
        setPlaybackUrl('');
        setPlaybackUrls([]);
        return;
      }

      try {
        const responses = await Promise.all(
          playbackSourceUrls.map(async (url) => {
            if (!url) return '';

            try {
              const response = await issueCloudFrontSignedCookies(url);
              return response?.url || url;
            } catch {
              return url;
            }
          }),
        );

        if (!cancelled) {
          setPlaybackUrls(responses);
          setPlaybackUrl(responses[clipIndex] || responses[0] || '');
        }
      } catch {
        if (!cancelled) {
          setPlaybackUrls(playbackSourceUrls);
          setPlaybackUrl(playbackSourceUrls[clipIndex] || playbackSourceUrls[0] || '');
        }
      }
    };

    preparePlaybackUrls();

    return () => {
      cancelled = true;
    };
  }, [clipIndex, playbackSourceUrls]);

  useEffect(() => {
    if (safari) {
      videoPlayerRef.current = safariPlayerRefs.current[clipIndex] || null;
    }
  }, [safari, clipIndex, playbackUrls]);

  const switchFullScreen = () => {
    if (!fullScreen.active) {
      fullScreen.enter();
    } else {
      fullScreen.exit();
    }
  };

  useHotkeys('f', () => switchFullScreen());
  useHotkeys('m', () =>
    {
      const nextVolume =
        composerPlayControl.composerControls.volume === 0 ? 1 : 0;
      rememberManualVolumeOverrideForFeed(feedId, nextVolume);
      dispatch(
        updateComposerPlayControls({
          ...composerPlayControl.composerControls,
          volume: nextVolume,
        }),
      );
    },
  );
  useEffect(() => {
    window.addEventListener('keydown', (e) => {
      if (e.keyCode === 9) {
        // keyCode 9 represents the Tab key
        e.preventDefault();
      }
    });
  }, []);

  const displayControls = () => {
    setShowControls(true);
  };

  const hideControls = () => {
    setShowControls(false);
  };

  const playPauseHandler = () => {
    dispatch(toFollowPlayhead(!composerPlayControl.composerControls.playing));
    dispatch(
      updateComposerPlayControls({
        ...composerPlayControl.composerControls,
        playing: !composerPlayControl.composerControls.playing,
      }),
    );
  };

  const seekPlayerTo = (seconds: number) => {
    if (!Number.isFinite(seconds) || seconds < 0) return;
    pendingSeekTargetRef.current = seconds;
    ignoreProgressUntilRef.current = Date.now() + 1200;

    if (safari) {
      safariPlayerRefs.current.forEach((player) => player?.seekTo(seconds, 'seconds'));
      return;
    }

    videoPlayerRef.current?.seekTo(seconds, 'seconds');
  };
  const sidebarPlayer = useSidebarPlayerSeek(seekPlayerTo);

  const jumpToTaggedSelectedClip = (clipId: string) => {
    setLoading(true);
    const urlParams = document.location.search;
    let clipToJump: TClip;
    if (clipId && composerRows.length) {
      setTimeout(() => {
        composerRows.forEach((row) => {
          row.clips.forEach((c) => {
            if (c.id === clipId) {
              clipToJump = c;
            }
          });
        });
        if (
          // @ts-ignore
          clipToJump &&
          typeof clipToJump?.clipIndex === 'number' &&
          typeof clipToJump?.rowIndex === 'number'
        ) {
          dispatch(updateSelectedMultipleClips(false));
          dispatch(
            updateCurrentSelectedComposerClips([
              {
                ...clipToJump,
                clipIndex: clipToJump.clipIndex,
                rowIndex: clipToJump.rowIndex,
              },
            ]),
          );
          dispatch(updateCustomTime(clipToJump.time_start));
          dispatch(
            setComposerMarkerPosition({
              clipIndex: clipToJump.clipIndex,
              rowIndex: clipToJump.rowIndex,
            }),
          );
          dispatch(
            updateComposerPlayControls({
              ...composerPlayControl.composerControls,
              playedSeconds: clipToJump.time_start,
              playing: !!autoPlayFromQuery || composerPlayControl.composerControls.playing,
            }),
          );
          const paramsObj = parseQueryString(urlParams);
          delete paramsObj.clipToJump;
          delete paramsObj.autoplay;
          const queryString = buildQueryString(paramsObj);
          window.history.replaceState(null, '', `?${queryString}`);
          dispatch(triggerRefresh());
          seekPlayerTo(clipToJump.time_start);
        }
        setLoading(false);
      }, 4000);
    }
  };

  const shouldIgnoreProgress = (playedSeconds: number) => {
    const target = pendingSeekTargetRef.current;
    if (target === null) return false;

    if (Math.abs(playedSeconds - target) <= 0.35) {
      pendingSeekTargetRef.current = null;
      ignoreProgressUntilRef.current = 0;
      return false;
    }

    if (Date.now() <= ignoreProgressUntilRef.current) {
      return true;
    }

    pendingSeekTargetRef.current = null;
    ignoreProgressUntilRef.current = 0;
    return false;
  };

  useEffect(() => {
    if (feedId?.length && composerRows.length && clipToJumpQuery?.length && clipToJumpQuery !== 'undefined') {
      jumpToTaggedSelectedClip(clipToJumpQuery);
    }
  }, [feedId, composerRows, clipToJumpQuery]);

  useEffect(() => {
    if (!autoPlayFromQuery) { consumedAutoplayRef.current = null; return; }
    if (clipToJumpQuery && clipToJumpQuery !== 'undefined') return;
    const navigation = `${feedId}:${autoPlayFromQuery}`;
    if (!activePlaybackUrl || String(composerPlayControl.currentComposer?.id) !== feedId || consumedAutoplayRef.current === navigation) return;
    consumedAutoplayRef.current = navigation;
    if (!composerControlsRef.current.playing) dispatch(updateComposerPlayControls({ ...composerControlsRef.current, playing: true }));
  }, [autoPlayFromQuery, clipToJumpQuery, feedId, composerPlayControl.currentComposer?.id, activePlaybackUrl, dispatch]);

  const onProgressHandler = ({
    loaded,
    loadedSeconds,
    played,
    playedSeconds,
  }: {
    loaded: number;
    loadedSeconds: number;
    played: number;
    playedSeconds: number;
  }) => {
    if (shouldIgnoreProgress(playedSeconds)) return;

    dispatch(
      updateComposerPlayControls({
        ...composerPlayControl.composerControls,
        loaded,
        loadedSeconds,
        played,
        playedSeconds,
      }),
    );
  };

  // clip switch logic START
  const urlParams = document.location.search;

  useEffect(() => {
    const clipInd = findSearchValue('clip');
    if (clipInd) {
      setClipIndex(+clipInd);
    }
  }, [urlParams]);

  const angleLockClip = composerRows[composerPlayControl.currentRowIndex]?.clips[composerPlayControl.currentClipIndex];
  const switchVideoClip = useCallback(
    (targetClipIndex?: number, targetTime?: number) => {
      let nextClipIndex = targetClipIndex ?? clipIndex;

      if (targetClipIndex == null) {
        for (let offset = 1; offset <= clipsCount + 1; offset++) {
          const candidateIndex = (clipIndex + offset) % (clipsCount + 1);
          if (angleVideos[candidateIndex]) {
            nextClipIndex = candidateIndex;
            break;
          }
        }
      }

      if (!angleVideos[nextClipIndex] || nextClipIndex === clipIndex) return;

      if (targetTime != null) {
        pendingAngleSeekRef.current = safari ? null : {
          videoId: String(angleVideos[nextClipIndex].id), clipId: String(angleLockClip?.id), seconds: targetTime,
        };
        seekPlayerTo(targetTime);
      } else {
        pendingAngleSeekRef.current = null;
      }

      const urlObject = parseQueryString(window.location.search);
      urlObject.clip = `${nextClipIndex}`;
      urlObject.clipId = angleVideos[nextClipIndex].id;
      window.history.replaceState(null, '', `?${buildQueryString(urlObject)}`);
      setClipIndex(nextClipIndex);
      dispatch(triggerRefresh());
    },
    [angleVideos, clipIndex, clipsCount, dispatch, safari, angleLockClip?.id],
  );

  const clipAngleVideos = angleLockClip?.videos?.length ? angleLockClip.videos : currentComposerVideos;
  const angleLockSeconds = composerPlayControl.composerControls.playedSeconds;
  const drawingPlaybackClip = angleLockClip && clipAngleVideos.some((video) => String(video.id) === String(angleVideos[clipIndex]?.id)) ? angleLockClip : undefined;
  const selectedDrawingClip = drawingPlaybackClip && angleLockSeconds >= drawingPlaybackClip.time_start && angleLockSeconds <= drawingPlaybackClip.time_end &&
    currentSelectedComposerClips.some((clip) => String(clip.id) === String(drawingPlaybackClip.id))
    ? drawingPlaybackClip : undefined;
  const drawingAngleOverrideClip = useRef<string | null>(null);
  const angleLockReady = !!angleVideos[clipIndex] && clipIndex === Number(findSearchValue('clip') || 0) &&
    clipAngleVideos.length === angleVideos.length && clipAngleVideos.every((video, index) =>
      String(video?.id) === String(angleVideos[index]?.id));
  useClipAngleLock({
    feedId,
    clip: angleLockClip,
    videos: angleVideos,
    angleIndex: clipIndex,
    ready: angleLockReady,
    switchAngle: (index) => {
      if (drawingAngleOverrideClip.current !== String(angleLockClip?.id)) switchVideoClip(index, angleLockSeconds);
    },
  });
  useEffect(() => {
    if (angleLockReady) drawingAngleOverrideClip.current = null;
  }, [angleLockReady, angleLockClip?.id]);

  useHotkeys(
    ['1', '2', '3', '4', 'shift+1', 'shift+2', 'shift+3', 'shift+4'],
    (event) => {
      if (event.repeat) return;
      const angleKey = /^(?:Digit|Numpad)([1-4])$/.exec(event.code)?.[1] ?? event.key;
      switchVideoClip(Number(angleKey) - 1);
    },
    { ignoreEventWhen: ignoreAngleShortcut },
  );

  useHotkeys(
    'c',
    (event) => {
      if (event.repeat) return;
      switchVideoClip();
    },
    { ignoreEventWhen: ignoreAngleShortcut },
  );

  useEffect(() => {
    if (angleVideos[clipIndex] || !angleVideos.length) return;

    const firstAvailableAngle = angleVideos.findIndex(Boolean);
    if (firstAvailableAngle >= 0) switchVideoClip(firstAvailableAngle);
  }, [angleVideos, clipIndex, switchVideoClip]);

  useEffect(() => {
    const handleMobileSwitchAngle = () => {
      if (window.innerWidth < 768) switchVideoClip();
    };
    window.addEventListener(MOBILE_SWITCH_ANGLE_EVENT, handleMobileSwitchAngle);
    return () => window.removeEventListener(MOBILE_SWITCH_ANGLE_EVENT, handleMobileSwitchAngle);
  }, [switchVideoClip]);

  const duration = videoPlayerRef.current ? videoPlayerRef.current.getDuration() : '00:00';
  const prevCustomTimeRef = useRef<number | null>(null);
  useEffect(() => {
    if (duration && typeof duration === 'number' && duration > 0) {
      dispatch(updateVideoLength(duration));
      dispatch(saveVideoLength(duration));
    }

    if (
      currentComposerVideos.length > 0 &&
      composerRows.length > 0 &&
      videoPlayerRef?.current &&
      isNumber(customTime) &&
      isFinite(customTime) &&
      prevCustomTimeRef.current !== customTime
    ) {
      prevCustomTimeRef.current = customTime;
      seekPlayerTo(customTime);
    }
  }, [playbackUrl, duration, currentComposerVideos, customTime, refreshTrigger]);

  const onVideoQualitySet = (videoQualityArr: [string, string]) => {
    dispatch(updateSelectedVideoQuality({ quality: videoQualityArr[0], url: videoQualityArr[1] }));
    setVideoQualityToLocalStorage(`${videoQualityArr[0]}`);
  };
  useEffect(() => {
    if (videoData) {
      dispatch(updateCurrentVideoThatInFrame(videoData));
    }
    const rememberedQuality = getVideoQualityFromLocalStorage(); // string like "1080"
    if (videoData) {
      if (rememberedQuality?.length && videoData.s3_keys[rememberedQuality] && videoData.s3_keys[rememberedQuality]?.length) {
        onVideoQualitySet([rememberedQuality, videoData.s3_keys[rememberedQuality]]); //['1080', 'www.video.com/video.mp4']
      } else {
        let highestKey = null;
        let highestValue = null;
        for (const key in videoData?.s3_keys) {
          if (!highestKey || parseInt(key) > parseInt(highestKey)) {
            highestKey = key;
            highestValue = videoData.s3_keys[key];
          }
        }
        if (highestKey && highestValue) {
          onVideoQualitySet([highestKey, highestValue]);
        }
      }
    }
  }, [videoData]);

  const switchNotesCallBack = () => {
    dispatch(updateShowNotes(!showNotes));
  };

  const heightOfPlayer = useMemo(() => {
    if (showNotes && (notes?.length || qualifiers?.length)) {
      // return 'calc(100% - 70px)';
      return '100%';
    } else {
      return '100%';
    }
  }, [videoPlayerHeight, showNotes, notes, qualifiers]);

  const [showPauseIcon, setShowPauseIcon] = useState(false);
  const [hidePauseUntilMouseMove, setHidePauseUntilMouseMove] = useState(false);
  const timeoutRef = useRef<any>(null);

  useEffect(() => {
    if (composerPlayControl.composerControls.playing) {
      setHidePauseUntilMouseMove(false);
      setShowPauseIcon(false);
      clearTimeout(timeoutRef.current);
      return;
    }

    // Show icon when paused unless hidden by frame-step.
    if (!hidePauseUntilMouseMove) {
      setShowPauseIcon(true);
      startHideTimer();
    }
  }, [composerPlayControl.composerControls.playing, hidePauseUntilMouseMove]);

  useEffect(() => {
    const handleHidePauseOverlay = (event: Event) => {
      const detail = (event as CustomEvent<{ context?: string }>).detail;
      if (detail?.context !== 'composer') return;
      setHidePauseUntilMouseMove(true);
      setShowPauseIcon(false);
      clearTimeout(timeoutRef.current);
    };

    window.addEventListener(PAUSE_OVERLAY_HIDE_EVENT, handleHidePauseOverlay as EventListener);
    return () => {
      window.removeEventListener(PAUSE_OVERLAY_HIDE_EVENT, handleHidePauseOverlay as EventListener);
    };
  }, []);

  useEffect(
    () => () => {
      clearTimeout(timeoutRef.current);
    },
    [],
  );

  const startHideTimer = () => {
    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => setShowPauseIcon(false), 5000);
  };

  const handleMouseMove = () => {
    if (!composerPlayControl.composerControls.playing) {
      if (hidePauseUntilMouseMove) {
        setHidePauseUntilMouseMove(false);
      }
      setShowPauseIcon(true);
      startHideTimer();
    }
  };
  const bufferStartHandler = () => {
    dispatch(updateComposerPlayControls({ ...composerPlayControl.composerControls, buffer: true }));
  };

  const bufferEndHandler = () => {
    dispatch(updateComposerPlayControls({ ...composerPlayControl.composerControls, buffer: false }));
  };

  const disableBuffering = () => {
    const video = videoPlayerRef.current?.getInternalPlayer();
    if (video instanceof HTMLVideoElement) video.playbackRate = composerControlsRef.current.playbackRate;
    dispatch(updateComposerPlayControls({ ...composerPlayControl.composerControls, buffer: false }));
    const pending = pendingAngleSeekRef.current;
    if (pending && pending.videoId === String(angleVideos[clipIndex]?.id) && pending.clipId === String(angleLockClip?.id)) {
      pendingAngleSeekRef.current = null;
      seekPlayerTo(pending.seconds);
    } else {
      if (pending?.clipId !== String(angleLockClip?.id)) pendingAngleSeekRef.current = null;
      if (customTime > 0) seekPlayerTo(customTime);
    }
  };

  const jumpToDrawingMarker = (comment: COMMENT, automatic = false): boolean => {
    const matches = (clip: TClip) => (comment.clip_id == null || String(comment.clip_id) === String(clip.id)) &&
      comment.timestamp >= clip.time_start && comment.timestamp <= clip.time_end &&
      (clip.videos?.length ? clip.videos : currentComposerVideos).some((video) => String(video.id) === comment.drawing?.videoId);
    let rowIndex = composerPlayControl.currentRowIndex;
    let targetClipIndex = composerPlayControl.currentClipIndex;
    if (!angleLockClip || !matches(angleLockClip)) {
      rowIndex = composerRows.findIndex((row) => row.clips.some(matches));
      targetClipIndex = rowIndex >= 0 ? composerRows[rowIndex].clips.findIndex(matches) : -1;
    }
    const targetClip = composerRows[rowIndex]?.clips[targetClipIndex];
    if (comment.clip_id != null && !targetClip) return false;
    const videos = targetClip?.videos?.length ? targetClip.videos : currentComposerVideos;
    const index = videos.findIndex((video) => String(video.id) === comment.drawing?.videoId);
    if (index < 0) return false;
    if (targetClip) {
      if (String(targetClip.id) !== String(angleLockClip?.id)) drawingAngleOverrideClip.current = String(targetClip.id);
      if (!automatic && comment.clip_id != null && !currentSelectedComposerClips.some((clip) => String(clip.id) === String(targetClip.id))) {
        dispatch(updateCurrentSelectedComposerClips([{ ...targetClip, rowIndex, clipIndex: targetClipIndex }]));
        dispatch(updateSelectedMultipleClips(false));
      }
      dispatch(setComposerMarkerPosition({ rowIndex, clipIndex: targetClipIndex }));
      dispatch(updateComposerVideoCLips(targetClip.videos?.length ? targetClip.videos : null));
    }
    if (!automatic && comment.clip_id == null) {
      dispatch(updateCurrentSelectedComposerClips([]));
      dispatch(updateSelectedMultipleClips(false));
    }
    const params = parseQueryString(window.location.search);
    params.clip = String(index); params.clipId = videos[index].id;
    window.history.replaceState(null, '', `?${buildQueryString(params)}`);
    setClipIndex(index);
    pendingAngleSeekRef.current = !safari && String(angleVideos[clipIndex]?.id) !== String(videos[index].id) ? {
      videoId: String(videos[index].id), clipId: String(targetClip?.id || angleLockClip?.id), seconds: comment.timestamp,
    } : null;
    seekPlayerTo(comment.timestamp);
    dispatch(updateCustomTime(comment.timestamp));
    dispatch(updateComposerPlayControls({ ...composerPlayControl.composerControls, playedSeconds: comment.timestamp, playing: false }));
    dispatch(triggerRefresh());
    return true;
  };

  return (
    <>
      <div
        style={{ height: heightOfPlayer }}
        className={css.wrapper}
        onMouseOver={displayControls}
        onMouseLeave={hideControls}
        onMouseMove={handleMouseMove}
      >
        <FullScreen handle={fullScreen} className={css.fullScreenWrapper}>
          <div className={css.videoStage} data-drawing-stage>
          <FavouriteFeedback video />
          {safari ? (
            playbackUrls.map(
              (url, index) =>
                !!url && (
                  <ReactPlayer
                    key={`${feedId || 'composer'}-${index}-${url}`}
                    className={css.player}
                    width="100%"
                    height="100%"
                    ref={(player) => {
                      safariPlayerRefs.current[index] = player;
                      if (index === clipIndex) {
                        videoPlayerRef.current = player;
                      }
                    }}
                    progressInterval={100}
                    onProgress={index === clipIndex ? onProgressHandler : undefined}
                    onEnded={index === clipIndex ? () => sidebarPlayer.onEnded(videoPlayerRef.current?.getCurrentTime() ?? 0) : undefined}
                    playing={composerPlayControl.composerControls.playing}
                    playbackRate={composerPlayControl.composerControls.playbackRate}
                    volume={index === clipIndex ? composerPlayControl.composerControls.volume : 0}
                    muted={index !== clipIndex || composerPlayControl.composerControls.volume === 0}
                    onBuffer={index === clipIndex ? bufferStartHandler : undefined}
                    onBufferEnd={index === clipIndex ? bufferEndHandler : undefined}
                    onSeek={index === clipIndex ? bufferEndHandler : undefined}
                    onReady={index === clipIndex ? disableBuffering : undefined}
                    onError={index === clipIndex ? playbackRecovery.onError : undefined}
                    url={url}
                    responsetype="stream"
                    pip={false}
                    playsinline
                    style={{
                      opacity: index === clipIndex ? 1 : 0,
                      pointerEvents: index === clipIndex ? 'auto' : 'none',
                      transition: 'opacity 120ms linear',
                      zIndex: index === clipIndex ? 1 : 0,
                    }}
                    config={{
                      file: {
                        hlsOptions: {
                          xhrSetup: (xhr: XMLHttpRequest) => {
                            xhr.withCredentials = shouldUseCredentialsForMediaUrl(url);
                          },
                        },
                        attributes: {
                          playsInline: true,
                          onContextMenu: (e: React.SyntheticEvent) => e.preventDefault(),
                          crossOrigin: shouldUseCredentialsForMediaUrl(url)
                            ? 'use-credentials'
                            : 'anonymous',
                        },
                      },
                    }}
                  />
                ),
            )
          ) : (
            <ReactPlayer
              className={css.player}
              width="100%"
              height="100%"
              ref={videoPlayerRef}
              progressInterval={100}
              onProgress={onProgressHandler}
              onEnded={() => sidebarPlayer.onEnded(videoPlayerRef.current?.getCurrentTime() ?? 0)}
              playing={composerPlayControl.composerControls.playing}
              playbackRate={composerPlayControl.composerControls.playbackRate}
              volume={composerPlayControl.composerControls.volume}
              onBuffer={bufferStartHandler}
              onBufferEnd={bufferEndHandler}
              onSeek={bufferEndHandler}
              onReady={disableBuffering}
              onError={playbackRecovery.onError}
              url={playbackUrl}
              responsetype="stream"
              pip={false}
              playsinline
              config={{
                file: {
                  hlsOptions: {
                    xhrSetup: (xhr: XMLHttpRequest) => {
                      xhr.withCredentials = shouldUseCredentialsForMediaUrl(playbackUrl);
                    },
                  },
                  attributes: {
                    playsInline: true,
                    onContextMenu: (e: React.SyntheticEvent) => e.preventDefault(),
                    crossOrigin: shouldUseCredentialsForMediaUrl(playbackUrl)
                      ? 'use-credentials'
                      : 'anonymous',
                  },
                },
              }}
            />
          )}

          {composerPlayControl.composerControls.buffer && activePlaybackUrl && (
            <div className={css.videoLinearLoader}>
              <CircularProgress color="inherit" />
            </div>
          )}

          <div className={css.videoAngleBtn} onClick={() => switchVideoClip()}>
            <div className={css.index}>{clipIndex + 1}</div>
          </div>
          <VideoDrawing playing={composerPlayControl.composerControls.playing} playerRef={videoPlayerRef}
            reservePlayerControls
            sourceKey={`${feedId}:${angleVideos[clipIndex]?.id}`}
            feedId={feedId || ''} videoId={String(angleVideos[clipIndex]?.id || '')}
            videoIds={angleVideos.map((video) => String(video.id))}
            seconds={composerPlayControl.composerControls.playedSeconds}
            selectedClip={selectedDrawingClip ? {
              id: String(selectedDrawingClip.id), time_start: selectedDrawingClip.time_start, time_end: selectedDrawingClip.time_end,
              label: `${selectedDrawingClip.topicName || composerRows[composerPlayControl.currentRowIndex]?.name || 'Selected clip'}, clip ${composerPlayControl.currentClipIndex + 1}`,
            } : undefined}
            playbackClip={drawingPlaybackClip ? {
              id: String(drawingPlaybackClip.id), time_start: drawingPlaybackClip.time_start, time_end: drawingPlaybackClip.time_end,
              label: `${drawingPlaybackClip.topicName || composerRows[composerPlayControl.currentRowIndex]?.name || 'Clip'}, clip ${composerPlayControl.currentClipIndex + 1}`,
            } : undefined}
            hasSelectedClips={!!currentSelectedComposerClips.length}
            autoStop={!composerPlayControl.composerControls.seeking}
            jumpToMarker={jumpToDrawingMarker}
            pause={() => dispatch(updateComposerPlayControls({ ...composerPlayControl.composerControls, playing: false }))} />
          <div className={css.shadowPlayControl} onClick={playPauseHandler} />

          <div className={css.playPauseWrapper} data-video-play-overlay>
            {!composerPlayControl.composerControls.playing && (
              <PlayCircleOutlineOutlinedIcon
                className={cx(css.playIcon, !showPauseIcon ? css.hide : '')}
                style={{ display: 'block !important' }}
                onClick={() => playPauseHandler()}
              />
            )}
          </div>
          <div>
            {showNotes && !fullScreen.active && ((notes && notes.length > 0) || (qualifiers && qualifiers.length > 0)) && (
              <div
                className={cx(
                  css.notes,
                  !showSidebar && !showCommentsSection ? css.notesWithoutBoth : '',
                  showSidebar && !showCommentsSection ? css.notesWithoutComments : '',
                  !showSidebar && showCommentsSection ? css.notesWithouSidebar : '',
                )}
                style={{ bottom: showControls ? '48px' : '0px' }}
              >
                <NotesForVideo className={css.notesInner} switchShowNotes={switchNotesCallBack} showNotes={showNotes} />
              </div>
            )}
            <div className={cx(css.controls, (showControls || fullScreen.active) && css.showControls)}>
              <ComposerVideoControls fullScreenHandler={switchFullScreen} activeFullScreen={fullScreen.active} videoData={videoData} />
            </div>
          </div>
          </div>
        </FullScreen>
        <FullPageLoader loading={loading} />
      </div>
    </>
  );
};

export default ComposerVideoPlayer;
